// Ekran 08 — Filo Yönetimi.
// Yerleşim ref_files/btys-erp-08-filo.html'den birebir:
//   .crumb · .page-h · .kstrip(5) · .list-grid(.card + .insp) · sefer programı kartı
//
// Ekranda hiçbir sayı hesaplanmıyor: TL/km, L/100, sapma ve vade bantlarının
// tamamı sunucudan geliyor (Btys.Domain/Calc/Filo.cs).

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Kabuk from '../../components/Kabuk'
import { useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { useFitInsp } from '../../lib/useFitInsp'
import { tr, trSaat, trTarih, trTarihSaat, sayiyaCevir } from '../../lib/trFormat'
import { ApiHatasi, jetonAl } from '../../lib/api'
import { useYetki, YetkiKapisi } from '../../lib/yetki'

import FiloPaneli, { type PanelSecimi } from './FiloPaneli'
import { BakimDrawer, DolumDrawer, SeferDrawer, VarlikDrawer } from './FiloDrawerlari'
import {
  aracRozeti, bakimRozeti, CEZA_DURUMLARI, dorseRozeti, gunAdi, gunAy, sapmaHucresi,
  SEFER_DURUMLARI, SEFER_NOKTASI, vadeHucresi,
} from './ortak'
import { ekYukle, filo, type Arac, type Bakim, type SeferDurumu } from './api'

type Segment = 'arac' | 'dorse' | 'surucu' | 'yakit' | 'bakim' | 'ceza'

const SEGMENTLER: [Segment, string][] = [
  ['arac', 'Araçlar'],
  ['dorse', 'Dorseler'],
  ['surucu', 'Sürücüler'],
  ['yakit', 'Yakıt'],
  ['bakim', 'Bakım'],
  ['ceza', 'Cezalar'],
]

/** Seed'in program günü; "bugün" ilerlese de hikâye bu günde duruyor. */
const PROGRAM_GUNU = '2026-08-15'

function Icerik() {
  const [segment, setSegment] = useState<Segment>('arac')
  const [secim, setSecim] = useState<PanelSecimi>({ tur: 'yok' })
  const panel = useFitInsp<HTMLDivElement>()
  const { ac, kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const { izinVar } = useYetki()
  const yazabilir = izinVar('FiloCariler', 'yaz')

  const { data: ozet } = useQuery({ queryKey: ['filo-ozet'], queryFn: filo.ozet })
  const { data: sayaclar } = useQuery({ queryKey: ['filo-sayaclar'], queryFn: filo.sayaclar })
  const { data: araclar = [] } = useQuery({ queryKey: ['araclar'], queryFn: filo.araclar })
  const { data: dorseler = [] } = useQuery({ queryKey: ['dorseler'], queryFn: filo.dorseler })
  const { data: suruculer = [] } = useQuery({ queryKey: ['suruculer'], queryFn: filo.suruculer })
  const { data: evraklar = [] } = useQuery({ queryKey: ['evraklar'], queryFn: filo.evraklar })
  const { data: cezalar = [] } = useQuery({ queryKey: ['cezalar'], queryFn: filo.cezalar })
  const { data: dolumlar = [] } = useQuery({ queryKey: ['dolumlar'], queryFn: filo.dolumlar })
  const { data: bakimlar = [] } = useQuery({ queryKey: ['bakimlar'], queryFn: filo.bakimlar })
  const { data: seferler = [] } = useQuery({
    queryKey: ['seferler', PROGRAM_GUNU],
    queryFn: () => filo.seferler(PROGRAM_GUNU),
  })

  // Referans açılışta `selA("42 KE 1201")` çağırıyor — sağ panel boş gelmiyor.
  // Plakayı koda gömmek yerine listenin ilk aracı seçiliyor (sunucu sırası
  // zaten plaka sırası). Bağımlılıkta YALNIZ `ilkAracId` var: `secim` de
  // eklenseydi segment değişiminin bıraktığı boş paneli anında yeniden
  // doldurur, referansın `setSeg` davranışı bozulurdu.
  const ilkAracId = araclar[0]?.id
  useEffect(() => {
    if (ilkAracId && secim.tur === 'yok') setSecim({ tur: 'arac', id: ilkAracId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ilkAracId])

  function tazele() {
    for (const k of ['filo-ozet', 'filo-sayaclar', 'araclar', 'dorseler', 'suruculer',
      'evraklar', 'cezalar', 'dolumlar', 'bakimlar', 'seferler']) {
      kuyruk.invalidateQueries({ queryKey: [k] })
    }
  }

  function hata(h: unknown, varsayilan: string) {
    if (!(h instanceof ApiHatasi)) return toast(varsayilan)
    const detay = h.detay?.length ? ` — ${h.detay.join(' · ')}` : ''
    toast(`${h.message}${detay}`)
  }

  // --------------------------------------------------------------- eylemler

  const seferDurum = useMutation({
    mutationFn: (g: { id: string; durum: SeferDurumu; gercekT: number | null }) =>
      filo.seferDurum(g.id, { durum: g.durum, gercekT: g.gercekT }),
    onSuccess: (s) => { tazele(); toast(s.mesaj) },
    onError: (h) => { hata(h, 'Sefer durumu güncellenemedi'); tazele() },
  })

  const bakimDurum = useMutation({
    mutationFn: (g: { bakim: Bakim; durum: string }) =>
      filo.bakimGuncelle(g.bakim.id, {
        tarih: g.bakim.tarih, sahipTip: g.bakim.sahipTip, sahipId: g.bakim.sahipId,
        tur: g.bakim.tur, aciklama: g.bakim.aciklama, servis: g.bakim.servis,
        sayacKm: g.bakim.sayacKm, tutar: g.bakim.tutar, durum: g.durum,
      }),
    onSuccess: (s) => { tazele(); toast(s.mesaj) },
    onError: (h) => hata(h, 'Bakım güncellenemedi'),
  })

  // Referanstaki `ata()` gibi ANINDA yazıyor: panelin iki select'inden biri
  // değişince diğeri mevcut hâliyle gidiyor (uç ikisini birlikte yazıyor).
  const atama = useMutation({
    mutationFn: (g: { arac: Arac; surucuId: string | null; dorseId: string | null }) =>
      filo.atama(g.arac.id, { surucuId: g.surucuId, dorseId: g.dorseId }),
    onSuccess: (_, g) => {
      tazele()
      toast(`${g.arac.plaka} ataması güncellendi — program ve rapor buna göre oluşur.`)
    },
    // Sunucu reddederse (K35) select eski değerine dönsün.
    onError: (h) => { hata(h, 'Atama güncellenemedi'); tazele() },
  })

  /** Panelden bakım ekine dosya yükler — §8-08 "gerçek upload + thumb". */
  const ekEkle = useMutation({
    mutationFn: async (g: { bakim: Bakim; dosyalar: File[] }) => {
      let yuklenen = 0
      for (const d of g.dosyalar) {
        try { await ekYukle(g.bakim.id, d, jetonAl()); yuklenen++ }
        catch (h) { toast(h instanceof Error ? h.message : `${d.name} yüklenemedi`) }
      }
      return yuklenen
    },
    onSuccess: (yuklenen) => {
      tazele()
      if (yuklenen > 0) toast(`${yuklenen} dosya kayda eklendi — evrak arşivine yüklendi.`)
    },
    onError: (h) => { hata(h, 'Ek yüklenemedi'); tazele() },
  })

  const cezaDurum = useMutation({
    mutationFn: (g: { id: string; govde: Record<string, unknown> }) =>
      filo.cezaKaydet(g.id, g.govde),
    onSuccess: () => { tazele(); toast('Ceza durumu güncellendi') },
    onError: (h) => hata(h, 'Ceza güncellenemedi'),
  })

  function seferEkleAc() {
    const id = ac({
      baslik: 'Sefer ekle — yükleme programı',
      altBaslik: 'Firma × atık türü tanımla; uygun araç filtrelenir',
      boyut: 'md',
      icerik: (
        <SeferDrawer
          araclar={araclar}
          dorseler={dorseler}
          kapat={() => kapat(id)}
          ekle={async (govde) => {
            try {
              await filo.seferEkle({ ...govde, tarih: PROGRAM_GUNU })
              tazele()
              kapat(id)
              toast('Sefer programa eklendi — araç ve sürücü bu saate atandı (K35).')
            } catch (h) { hata(h, 'Sefer eklenemedi') }
          }}
        />
      ),
    })
  }

  function dolumEkleAc() {
    const id = ac({
      baslik: 'Yakıt dolum kaydı',
      altBaslik: 'Sayaç km ile aralık tüketimi otomatik hesaplanır',
      boyut: 'md',
      icerik: (
        <DolumDrawer
          araclar={araclar}
          kapat={() => kapat(id)}
          ekle={async (govde) => {
            try {
              await filo.dolumEkle(govde)
              tazele()
              kapat(id)
              toast('Dolum kaydedildi — Motorin stok hareketi oluştu, sapma analizi güncellendi.')
            } catch (h) { hata(h, 'Dolum kaydedilemedi') }
          }}
        />
      ),
    })
  }

  function bakimEkleAc() {
    const id = ac({
      baslik: 'Bakım / onarım kaydı',
      altBaslik: 'Gider TL/km maliyetine işlenir · evrak ve resim eklenebilir',
      boyut: 'md',
      icerik: (
        <BakimDrawer
          araclar={araclar}
          dorseler={dorseler}
          kapat={() => kapat(id)}
          ekle={async (govde, dosyalar) => {
            try {
              const bakim = await filo.bakimEkle(govde)
              let yuklenen = 0
              for (const d of dosyalar) {
                try { await ekYukle(bakim.id, d, jetonAl()); yuklenen++ }
                catch (h) { toast(h instanceof Error ? h.message : `${d.name} yüklenemedi`) }
              }
              tazele()
              kapat(id)
              toast(`Bakım kaydı oluşturuldu — ${yuklenen} ek yüklendi` +
                (govde.durum === 'tamamlandi' ? ', TL/km güncellendi (K38).' : '.'))
            } catch (h) { hata(h, 'Bakım kaydedilemedi') }
          }}
        />
      ),
    })
  }

  function varlikEkleAc() {
    const id = ac({
      baslik: 'Araç / dorse / sürücü ekle',
      altBaslik: 'Filo varlık kaydı',
      boyut: 'md',
      icerik: (
        <VarlikDrawer
          kapat={() => kapat(id)}
          tamamlandi={(mesaj) => { tazele(); kapat(id); toast(mesaj) }}
        />
      ),
    })
  }

  // ----------------------------------------------------------------- tablo

  const sayac = (k: Segment) => {
    if (!sayaclar) return 0
    return { arac: sayaclar.arac, dorse: sayaclar.dorse, surucu: sayaclar.surucu,
      yakit: sayaclar.yakit, bakim: sayaclar.bakim, ceza: sayaclar.ceza }[k]
  }

  const altBilgi = useMemo(() => {
    switch (segment) {
      case 'arac': return `${araclar.length} araç`
      case 'dorse': return `${dorseler.length} dorse`
      case 'surucu': return `${suruculer.length} sürücü`
      case 'yakit': {
        const l = dolumlar.reduce((a, d) => a + d.litre, 0)
        const t = dolumlar.reduce((a, d) => a + (d.tutar ?? 0), 0)
        return `${dolumlar.length} dolum · Σ ${tr(l, 0)} L · ${tr(t, 0)} ₺`
      }
      case 'bakim':
        return `${bakimlar.length} kayıt · Σ ${tr(bakimlar.reduce((a, b) => a + (b.tutar ?? 0), 0), 0)} ₺`
      default:
        return `${cezalar.length} kayıt · Σ ${tr(cezalar.reduce((a, c) => a + (c.tutar ?? 0), 0), 0)} ₺`
    }
  }, [segment, araclar, dorseler, suruculer, dolumlar, bakimlar, cezalar])

  const planToplam = seferler.reduce((a, s) => a + (s.planT ?? 0), 0)
  const gercekToplam = seferler.reduce((a, s) => a + (s.gercekT ?? 0), 0)

  /** Sürücü tablosunun "Bugün t" / "Bugün sefer" kolonları — program gününden. */
  const surucuGunu = useMemo(() => {
    const m = new Map<string, { ton: number; toplam: number; tamam: number }>()
    for (const s of seferler) {
      if (!s.surucuId) continue
      const g = m.get(s.surucuId) ?? { ton: 0, toplam: 0, tamam: 0 }
      g.ton += s.gercekT ?? 0
      g.toplam++
      if (s.durum === 'tamamlandi') g.tamam++
      m.set(s.surucuId, g)
    }
    return m
  }, [seferler])

  return (
    <>
      <div className="crumb">Operasyon <b>/ Filo</b></div>
      <div className="page-h">
        <h1 className="page-t">Filo Yönetimi</h1>
        <div className="page-actions">
          <button
            className="btn btn-ghost"
            type="button"
            onClick={() => toast('Filo raporu dışa aktarma henüz bağlanmadı.')}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            Excel'e aktar
          </button>
          {yazabilir && (
            <button className="btn btn-pri" type="button" onClick={varlikEkleAc}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Araç / sürücü ekle
            </button>
          )}
        </div>
      </div>

      <div className="kstrip">
        <div className="k">
          <div className="k-l">Filo</div>
          <div className="k-v">{ozet ? `${ozet.aracSayisi}+${ozet.dorseSayisi}` : '—'}</div>
          <div className="k-s">araç · dorse · {ozet?.surucuSayisi ?? '—'} sürücü</div>
        </div>
        <div className="k">
          <div className="k-l">Bugünkü Sefer</div>
          <div className="k-v">
            {seferler.filter((x) => x.durum === 'tamamlandi').length}/{seferler.length}
          </div>
          <div className="k-s">tamamlanan / planlı</div>
        </div>
        <div className="k">
          <div className="k-l">Bugün Taşınan</div>
          <div className="k-v">{tr(gercekToplam, 1)}</div>
          <div className="k-s">t · kantar gerçekleşen</div>
        </div>
        <div className="k">
          <div className="k-l">Filo Ort. Maliyet</div>
          <div className="k-v">{tr(ozet?.ortalamaTlKm ?? null, 2)}</div>
          <div className="k-s">TL/km · yakıt+bakım</div>
        </div>
        <div className="k">
          <div className="k-l">Evrak Uyarısı</div>
          <div className={`k-v${ozet && ozet.evrakUyarisi > 0 ? ' amber' : ''}`}>
            {ozet?.evrakUyarisi ?? '—'}
          </div>
          <div className="k-s">30 gün içinde dolacak / geçmiş</div>
        </div>
      </div>

      <div className="list-grid">
        <div className="card">
          <div className="toolbar">
            <div className="seg">
              {SEGMENTLER.map(([k, etiket]) => (
                <button
                  key={k}
                  type="button"
                  className={`seg-i${segment === k ? ' on' : ''}`}
                  onClick={() => { setSegment(k); setSecim({ tur: 'yok' }) }}
                >
                  {etiket}<small>{sayac(k)}</small>
                </button>
              ))}
            </div>
            <div className="toolbar-right">
              {yazabilir && segment === 'yakit' && (
                <button className="btn btn-sec btn-sm" type="button" onClick={dolumEkleAc}>
                  + Dolum ekle
                </button>
              )}
              {yazabilir && segment === 'bakim' && (
                <button className="btn btn-sec btn-sm" type="button" onClick={bakimEkleAc}>
                  + Bakım kaydı
                </button>
              )}
              {' '}{altBilgi}
            </div>
          </div>

          <table className="grid">
            {segment === 'arac' && (
              <>
                <thead>
                  <tr>
                    <th style={{ width: 96 }}>Plaka</th>
                    <th>Araç</th>
                    <th>Atama</th>
                    <th className="num">Ay km</th>
                    <th className="num" style={{ width: 76 }}>TL/km</th>
                    <th style={{ width: 104 }}>Evrak</th>
                    <th style={{ width: 84 }}>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {araclar.map((a) => {
                    const [rSinif, rMetin] = aracRozeti(a.durum)
                    const [vSinif, vMetin] = vadeHucresi(a.evrak, true)
                    return (
                      <tr
                        key={a.id}
                        className={secim.tur === 'arac' && secim.id === a.id ? 'sel' : ''}
                        onClick={() => setSecim({ tur: 'arac', id: a.id })}
                      >
                        <td><span className="kod">{a.plaka}</span></td>
                        <td>
                          <div className="cell-name">{a.tip}</div>
                          <div className="cell-sub">{a.model} · {tr(a.sayacKm, 0)} km</div>
                        </td>
                        <td style={{ fontSize: 11 }}>
                          {a.surucuAd ?? <span style={{ color: 'var(--faint)' }}>sürücü yok</span>}
                          {a.dorsePlaka && <div className="cell-sub">{a.dorsePlaka}</div>}
                        </td>
                        <td className="num">{tr(a.ay.km, 0)}</td>
                        <td className="num" style={{ fontWeight: 600 }}>{tr(a.ay.tlKm, 2)}</td>
                        <td className={vSinif}>{vMetin}</td>
                        <td><span className={rSinif}>{rMetin}</span></td>
                      </tr>
                    )
                  })}
                </tbody>
              </>
            )}

            {segment === 'dorse' && (
              <>
                <thead>
                  <tr>
                    <th style={{ width: 96 }}>Plaka</th>
                    <th>Dorse</th>
                    <th>Uygunluk</th>
                    <th>Bağlı çekici</th>
                    <th style={{ width: 120 }}>Muayene</th>
                  </tr>
                </thead>
                <tbody>
                  {dorseler.map((d) => {
                    const [uSinif, uMetin] = dorseRozeti(d.uygunluk)
                    const [vSinif, vMetin] = vadeHucresi(d.muayene)
                    return (
                      <tr
                        key={d.id}
                        className={secim.tur === 'dorse' && secim.id === d.id ? 'sel' : ''}
                        onClick={() => setSecim({ tur: 'dorse', id: d.id })}
                      >
                        <td><span className="kod">{d.plaka}</span></td>
                        <td>
                          <div className="cell-name">{d.tip}</div>
                          <div className="cell-sub">{tr(d.hacim, 0)} m³</div>
                        </td>
                        <td><span className={uSinif}>{uMetin}</span></td>
                        <td style={{ fontSize: 11 }}>
                          {d.aracPlaka ?? <span style={{ color: 'var(--faint)' }}>boşta</span>}
                        </td>
                        <td className={vSinif}>{vMetin}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </>
            )}

            {segment === 'surucu' && (
              <>
                <thead>
                  <tr>
                    <th>Sürücü</th>
                    <th>Atanan araç</th>
                    <th className="num">Bugün t</th>
                    <th className="num">Bugün sefer</th>
                    <th style={{ width: 110 }}>Belge</th>
                    <th className="num" style={{ width: 70 }}>Puan</th>
                  </tr>
                </thead>
                <tbody>
                  {suruculer.map((s2) => {
                    const [vSinif, vMetin] = vadeHucresi(s2.belge, true)
                    const g = surucuGunu.get(s2.id)
                    return (
                      <tr
                        key={s2.id}
                        className={secim.tur === 'surucu' && secim.id === s2.id ? 'sel' : ''}
                        onClick={() => setSecim({ tur: 'surucu', id: s2.id })}
                      >
                        <td>
                          <div className="cell-name">{s2.ad}</div>
                          <div className="cell-sub">{s2.tel ?? '—'}</div>
                        </td>
                        <td style={{ fontSize: 11 }}>
                          {s2.aracPlaka ?? <span style={{ color: 'var(--faint)' }}>yedek</span>}
                        </td>
                        <td className="num">{tr(g?.ton ?? 0, 1)}</td>
                        <td className="num">{g?.tamam ?? 0}/{g?.toplam ?? 0}</td>
                        <td className={vSinif}>{vMetin}</td>
                        <td className="num">
                          <b>{tr(s2.puan, 0)}</b>
                          {s2.puan !== null && (
                            <div className="perfbar"><i style={{ width: `${s2.puan}%` }} /></div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </>
            )}

            {segment === 'yakit' && (
              <>
                <thead>
                  <tr>
                    <th style={{ width: 96 }}>Tarih</th>
                    <th style={{ width: 96 }}>Plaka</th>
                    <th>Sürücü · İstasyon</th>
                    <th className="num">Litre</th>
                    <th className="num">Tutar ₺</th>
                    <th className="num">Sayaç km</th>
                    <th className="num">Aralık</th>
                    <th className="num" style={{ width: 74 }}>L/100</th>
                    <th className="num" style={{ width: 80 }}>Sapma</th>
                  </tr>
                </thead>
                <tbody>
                  {dolumlar.map((d) => {
                    const [sSinif, sMetin] = sapmaHucresi(d.sapma, d.bant)
                    return (
                      <tr key={d.id} onClick={() => setSecim({ tur: 'arac', id: d.aracId })}>
                        {/* Kolon 96px: referans da "15.08 07:10" basıyor. Yılı
                            kaybetmemek için tam zaman title'da duruyor. */}
                        <td className="mono" style={{ fontSize: 11 }} title={trTarihSaat(d.zaman)}>
                          {gunAy(d.zaman)} {trSaat(d.zaman)}
                        </td>
                        <td><span className="kod">{d.aracPlaka}</span></td>
                        {/* Referans gibi iki satır: sürücü adı üstte, istasyon
                            altında `.cell-sub` ile — araç/bakım satırlarındaki
                            ad+alt bilgi hiyerarşisiyle aynı okuma. */}
                        <td style={{ fontSize: 11.5 }}>
                          {d.surucuAd ?? '—'}
                          <div className="cell-sub">{d.istasyon ?? '—'}</div>
                        </td>
                        <td className="num">{tr(d.litre, 0)}</td>
                        <td className="num">{tr(d.tutar, 0)}</td>
                        <td className="num">{tr(d.sayacKm, 0)}</td>
                        <td className="num">{tr(d.aralikKm, 0)}</td>
                        <td className="num" style={{ fontWeight: 600 }}>{tr(d.aralikL100, 1)}</td>
                        <td className={sSinif}>{sMetin}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </>
            )}

            {segment === 'bakim' && (
              <>
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Tarih</th>
                    <th style={{ width: 96 }}>Plaka</th>
                    <th>İşlem</th>
                    <th>Servis</th>
                    <th className="num" style={{ width: 76 }}>km</th>
                    <th className="num" style={{ width: 84 }}>Tutar ₺</th>
                    <th style={{ width: 120 }}>Ekler</th>
                    <th style={{ width: 98 }}>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {bakimlar.map((b) => {
                    const [bSinif, bMetin] = bakimRozeti(b.durum)
                    return (
                      <tr
                        key={b.id}
                        className={secim.tur === 'bakim' && secim.id === b.id ? 'sel' : ''}
                        onClick={() => setSecim({ tur: 'bakim', id: b.id })}
                      >
                        {/* Kolon 60px: referans da "14.08" basıyor. Yılı
                            kaybetmemek için tam tarih title'da — yakıt
                            kolonuyla aynı kalıp. */}
                        <td className="mono" style={{ fontSize: 11 }} title={trTarih(b.tarih)}>
                          {gunAy(b.tarih)}
                        </td>
                        <td><span className="kod">{b.sahipPlaka}</span></td>
                        <td>
                          <div className="cell-name">{b.tur}</div>
                          <div className="cell-sub">{b.aciklama}</div>
                        </td>
                        <td style={{ fontSize: 11 }}>{b.servis}</td>
                        <td className="num">{tr(b.sayacKm, 0)}</td>
                        <td className="num" style={{ fontWeight: 600 }}>{tr(b.tutar, 0)}</td>
                        <td>
                          {b.ekler.length === 0
                            ? <span className="cell-sub">—</span>
                            : <span className="badge stat-plan">📎 {b.ekler.length} dosya</span>}
                        </td>
                        <td><span className={bSinif}>{bMetin}</span></td>
                      </tr>
                    )
                  })}
                </tbody>
              </>
            )}

            {segment === 'ceza' && (
              <>
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Tarih</th>
                    <th style={{ width: 96 }}>Plaka</th>
                    <th>Sürücü</th>
                    <th>Madde</th>
                    <th className="num" style={{ width: 84 }}>Tutar ₺</th>
                    <th style={{ width: 104 }}>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {cezalar.map((c) => (
                    <tr key={c.id}>
                      <td className="mono" style={{ fontSize: 11 }}>{trTarih(c.tarih)}</td>
                      <td><span className="kod">{c.aracPlaka ?? '—'}</span></td>
                      <td style={{ fontSize: 11.5 }}>{c.surucuAd ?? '—'}</td>
                      <td style={{ fontSize: 11 }}>{c.madde}</td>
                      <td className="num" style={{ fontWeight: 600 }}>{tr(c.tutar, 0)}</td>
                      <td>
                        <select
                          className="in"
                          style={{ padding: '2px 5px', fontSize: 10.5 }}
                          value={c.durum}
                          disabled={!yazabilir}
                          onChange={(e) => cezaDurum.mutate({
                            id: c.id,
                            govde: {
                              tarih: c.tarih, aracId: c.aracId, surucuId: c.surucuId,
                              madde: c.madde, tutar: c.tutar, durum: e.target.value,
                            },
                          })}
                        >
                          {CEZA_DURUMLARI.map(([k, etiket]) => (
                            <option key={k} value={k}>{etiket}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </>
            )}
          </table>

          <div className="grid-foot">
            <span>
              {segment === 'yakit'
                ? 'Sapma = dolum aralığı tüketimi ↔ araç ay ortalaması · >%10 amber, >%20 kırmızı'
                : 'Filo varlık yönetimi — atamalar panelden yapılır'}
            </span>
          </div>
        </div>

        <aside className="insp card" ref={panel}>
          <FiloPaneli
            secim={secim}
            araclar={araclar}
            dorseler={dorseler}
            suruculer={suruculer}
            evraklar={evraklar}
            dolumlar={dolumlar}
            bakimlar={bakimlar}
            cezalar={cezalar}
            seferler={seferler}
            yazabilir={yazabilir}
            aracAc={(id) => setSecim({ tur: 'arac', id })}
            atamaDegis={(arac, g) => atama.mutate({ arac, ...g })}
            ekYukleyici={(bakim, dosyalar) => ekEkle.mutate({ bakim, dosyalar })}
            bakimDurumDegis={(bakim, durum) => bakimDurum.mutate({ bakim, durum })}
          />
        </aside>
      </div>

      {/* Sefer programı — tam genişlik, sağ panelin dışında. */}
      <div className="card" style={{ marginTop: 8 }}>
        <div className="toolbar">
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--navy-800)' }}>
            Bugünün Sefer &amp; Yükleme Programı — {gunAy(PROGRAM_GUNU)} {gunAdi(PROGRAM_GUNU)}
          </span>
          <span className="cell-sub">
            firma × atık türü tanımlı · sıvı yükler tanker gerektirir
          </span>
          <div className="toolbar-right">
            {yazabilir && (
              <button className="btn btn-sec btn-sm" type="button" onClick={seferEkleAc}>
                + Sefer ekle
              </button>
            )}
            {/* Sürücüye program gönderimi motorda yok (SMS/mobil altyapısı yok).
                Düğme referansın slotunda duruyor ama kapalı — sahte toast yerine
                nedeni title'da (bkz. docs/kararlar-ek.md). */}
            <button
              className="btn btn-pri btn-sm"
              type="button"
              disabled
              title="Sürücüye program gönderimi (SMS / mobil uygulama) henüz bağlı değil"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
              Programı sürücülere gönder
            </button>
          </div>
        </div>

        <table className="ptab prg">
          <thead>
            <tr>
              <th className="prg-t">Saat</th>
              <th>Araç + Dorse</th>
              <th>Sürücü</th>
              <th>Firma</th>
              <th>Atık Türü</th>
              <th className="num">Plan t</th>
              <th className="num">Gerçek t</th>
              <th style={{ width: 132 }}>Durum</th>
              <th style={{ width: 132 }}>Mal kabul</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {seferler.map((sf) => (
              <tr key={sf.id}>
                <td className="mono" style={{ fontWeight: 600 }}>
                  {sf.saat?.slice(0, 5) ?? '—'}
                </td>
                <td>
                  <span className="kod">{sf.aracPlaka ?? '—'}</span>
                  <div className="cell-sub">{sf.dorsePlaka ?? 'damper kasa'}</div>
                </td>
                <td style={{ fontSize: 11.5 }}>{sf.surucuAd ?? '—'}</td>
                <td style={{ fontSize: 11.5 }}>{sf.cariUnvan ?? '—'}</td>
                {/* Hijyen rozeti K19'un sefer ayağı: 70°C·60dk parti kaydı
                    gerektiren yük daha kantara gelmeden görünüyor. */}
                <td style={{ fontSize: 11.5 }}>
                  {sf.stokAd ?? '—'}
                  {sf.stokHij && <> <span className="src-b src-tesis">Hijyen</span></>}
                  {sf.stokKartiId && (
                    <> <span className="cell-sub">· {sf.stokSividir ? 'sıvı' : 'katı'}</span></>
                  )}
                </td>
                <td className="num">{tr(sf.planT, 1)}</td>
                <td className="num">
                  {sf.durum === 'tamamlandi' ? (
                    <input
                      className="gt"
                      defaultValue={tr(sf.gercekT, 1)}
                      disabled={!yazabilir}
                      onBlur={(e) => {
                        const v = sayiyaCevir(e.target.value)
                        if (Number.isFinite(v) && v !== sf.gercekT) {
                          seferDurum.mutate({ id: sf.id, durum: sf.durum, gercekT: v })
                        }
                      }}
                    />
                  ) : '—'}
                </td>
                {/* nowrap: nokta + 96px'lik select dar kolonda alt satıra
                    kaymasın (referans da bu hücreye nowrap veriyor). */}
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span style={{
                    display: 'inline-block', width: 8, height: 8, borderRadius: 99,
                    background: SEFER_NOKTASI[sf.durum], marginRight: 6,
                  }} />
                  <select
                    className="in"
                    style={{ padding: '2px 5px', fontSize: 10.5, width: 96 }}
                    value={sf.durum}
                    disabled={!yazabilir}
                    onChange={(e) => seferDurum.mutate({
                      id: sf.id, durum: e.target.value as SeferDurumu, gercekT: sf.gercekT,
                    })}
                  >
                    {SEFER_DURUMLARI.map(([k, etiket]) => (
                      // Taslağı açılmış sefer iptale çekilemez — çöp kutusu
                      // düğmesiyle aynı kısıt, iki yol tek kuralı izlesin.
                      <option key={k} value={k} disabled={k === 'iptal' && sf.lotId !== null}>
                        {etiket}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {/* K36. Taslakta belge numarası HENÜZ YOK — numara onayda
                      üretiliyor (ekran 02 ile aynı kalıp), o yüzden taslağın
                      iç kimliğini göstermek yerine durumu yazıyoruz. */}
                  {!sf.lotId ? (
                    <span className="cell-sub">—</span>
                  ) : sf.lotDurum === 'taslak' ? (
                    <span className="badge b-amber">Taslak açık</span>
                  ) : (
                    <span className="badge b-navy">{sf.lotBelgeNo}</span>
                  )}
                </td>
                <td>
                  {/* Referansın çöp kutusu satırı diziden SİLİYORDU; burada
                      sefer `iptal`e çekiliyor (K15 ruhu: silme değil ters durum).
                      Taslağı açılmış sefer kilitli — taslak mal kabulde duruyor,
                      iptal onu öksüz bırakırdı. */}
                  {yazabilir && (
                    <button
                      className="mini-btn"
                      type="button"
                      disabled={sf.lotId !== null || sf.durum === 'iptal'}
                      title={sf.lotId !== null
                        ? 'Mal kabul taslağı açılmış sefer programdan çıkarılamaz — taslağı ekran 02\'den reddedin'
                        : sf.durum === 'iptal'
                          ? 'Sefer zaten iptal'
                          : 'Seferi programdan çıkar (iptal)'}
                      onClick={() => seferDurum.mutate({
                        id: sf.id, durum: 'iptal', gercekT: sf.gercekT,
                      })}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      </svg>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="grid-foot">
          <span>
            Σ plan {tr(planToplam, 1)} t · Σ gerçek {tr(gercekToplam, 1)} t · {seferler.length} sefer
          </span>
          <span className="cell-sub" style={{ marginLeft: 12 }}>
            Tamamlanan seferler mal kabul taslağı oluşturur (K36) — LOT eşleşmesi kantarda
          </span>
        </div>
      </div>
    </>
  )
}

export default function FiloSayfasi() {
  return (
    <Kabuk listeBasligi="Filo Yönetimi">
      <YetkiKapisi modul="FiloCariler">
        <Icerik />
      </YetkiKapisi>
    </Kabuk>
  )
}
